import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../api/axios';
import Modal from '../components/shared/Modal';
import ConfirmModal from '../components/shared/ConfirmModal';

export default function Statuses() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editSt, setEditSt] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#0f74b9', is_active: 1, sort_order: 99 });

  const { data: statuses = [], isLoading } = useQuery({
    queryKey: ['statuses'],
    queryFn: () => api.get('/statuses').then(r => r.data),
  });

  const saveMut = useMutation({
    mutationFn: () => editSt
      ? api.put(`/statuses/${editSt.id}`, form)
      : api.post('/statuses', form),
    onSuccess: () => { toast.success(editSt ? 'Status updated' : 'Status added'); qc.invalidateQueries(['statuses']); setModalOpen(false); },
  });

  const deleteMut = useMutation({
    mutationFn: id => api.delete(`/statuses/${id}`),
    onSuccess: () => { toast.success('Status deleted'); qc.invalidateQueries(['statuses']); },
  });

  const open = (s = null) => {
    setEditSt(s);
    setForm(s ? { name: s.name, color: s.color, is_active: s.is_active, sort_order: s.sort_order } : { name: '', color: '#0f74b9', is_active: 1, sort_order: 99 });
    setModalOpen(true);
  };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
      <div className="page-header">
        <h2>Status Management</h2>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => open()}>+ Add Status</button>
      </div>
      <div className="tbl-wrap">
        <table>
          <thead><tr><th>Status Name</th><th>Color</th><th>Order</th><th>Active</th><th>Actions</th></tr></thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30 }}>Loading…</td></tr>
            ) : statuses.map(s => (
              <tr key={s.id}>
                <td><strong>{s.name}</strong></td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ width: 12, height: 12, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#6c757d' }}>{s.color}</span>
                  </div>
                </td>
                <td style={{ color: '#6c757d', fontSize: 12 }}>{s.sort_order}</td>
                <td><div className={`toggle ${s.is_active ? 'on' : ''}`}
                  onClick={() => api.put(`/statuses/${s.id}`, { is_active: s.is_active ? 0 : 1 }).then(() => qc.invalidateQueries(['statuses']))} role="presentation" /></td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" className="ico-btn" onClick={() => open(s)}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M11 2l3 3-9 9H2v-3l9-9z" /></svg>
                    </button>
                    <button type="button" className="ico-btn" onClick={() => setDeleteId(s.id)}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#dc3545" strokeWidth="1.5"><polyline points="2,4 14,4" /><path d="M5 4V2h6v2M6 7v5M10 7v5" /><rect x="3" y="4" width="10" height="10" rx="1" /></svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editSt ? 'Edit Status' : 'Add Status'}>
        <div className="form-group"><label>Status Name</label><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. On Hold" /></div>
        <div className="form-group">
          <label>Color</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <input type="color" value={form.color} onChange={e => set('color', e.target.value)}
              style={{ width: 42, height: 34, padding: 2, borderRadius: 5, border: '1px solid #ced4da', cursor: 'pointer' }} />
            <input value={form.color} onChange={e => set('color', e.target.value)}
              style={{ flex: 1, padding: '7px 9px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12, fontFamily: 'monospace' }} />
          </div>
        </div>
        <div className="form-group"><label>Sort Order</label>
          <input type="number" value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value, 10) || 0)}
            style={{ width: '100%', padding: '7px 9px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 13 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#495057' }}>Active</label>
          <div className={`toggle ${form.is_active ? 'on' : ''}`} onClick={() => set('is_active', form.is_active ? 0 : 1)} role="presentation" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => saveMut.mutate()} disabled={!form.name || saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : 'Save Status'}
          </button>
        </div>
      </Modal>

      <ConfirmModal open={!!deleteId} onClose={() => setDeleteId(null)}
        title="Delete this status?" message="Statuses with existing jobs cannot be deleted."
        confirmLabel="Delete" danger onConfirm={() => deleteMut.mutate(deleteId)} />
    </div>
  );
}
